import { z } from 'zod';
import { INBOX_SOURCES } from './inbox';
import { MoneyNum, QtyNum } from './numbers';

/* ─────────── Регистрация проекта: преддоговорная стадия, финансирование, карточка (ПФ-21…26) ─────────── */

const Money = z.number().finite();
/** Ввод сумм — с пределом, чтобы ошибка ввода не превращалась в сбой сервера */
const MoneyIn = MoneyNum;
const DateStr = z.iso.date();

export const FINANCING_KINDS = { credit: 'Кредит', bg: 'Банковская гарантия' } as const;
export type FinancingKind = keyof typeof FINANCING_KINDS;

/** Стадии проекта до подписания доходного договора (ПФ-23) */
export const PRECONTRACT_STAGES = ['preparation', 'tender'] as const;

export const EXCEPTION_STATUSES = {
  none: 'Нет',
  pending: 'На одобрении Директора',
  approved: 'Одобрено Директором',
  rejected: 'Отклонено',
} as const;
export type ExceptionStatus = keyof typeof EXCEPTION_STATUSES;

export const FinancingRowInput = z.object({
  kind: z.enum(['credit', 'bg']),
  source: z.string().trim().max(200).nullable().optional(),
  amount: MoneyIn.positive('Сумма больше нуля'),
  ratePct: z.number().min(0).max(100).nullable().optional(),
  dateFrom: DateStr.nullable().optional(),
  dateTo: DateStr.nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type FinancingRowInput = z.infer<typeof FinancingRowInput>;

export const CardRowInput = z.object({
  article: z.string().trim().min(2).max(200),
  counterpartyBin: z
    .string()
    .trim()
    .regex(/^\d{12}$/, 'БИН — 12 цифр')
    .nullable()
    .optional(),
  counterpartyName: z.string().trim().max(300).nullable().optional(),
  name: z.string().trim().min(1).max(500),
  unit: z.string().trim().min(1).max(30),
  quantity: QtyNum.nonnegative(),
  purchasePrice: MoneyIn.nonnegative().nullable().optional(),
  salePrice: MoneyIn.nonnegative().nullable().optional(),
});
export type CardRowInput = z.infer<typeof CardRowInput>;

export const PrecontractInput = z.object({
  requestNote: z.string().trim().max(4000).nullable().optional(),
  tenderNote: z.string().trim().max(4000).nullable().optional(),
  sourceNote: z.string().trim().max(4000).nullable().optional(),
  costCalcAmount: MoneyIn.nonnegative().nullable().optional(),
  costCalcNote: z.string().trim().max(4000).nullable().optional(),
  offerAmount: MoneyIn.nonnegative().nullable().optional(),
  offerSentOn: DateStr.nullable().optional(),
});
export type PrecontractInput = z.infer<typeof PrecontractInput>;

export const PrecontractAction = z.object({
  action: z.enum([
    'confirm_calc',
    'confirm_volumes',
    'request_exception',
    'approve_exception',
    'reject_exception',
  ]),
  reason: z.string().trim().max(4000).optional(),
  scope: z.string().trim().max(4000).optional(),
  cost: MoneyIn.nonnegative().optional(),
  terms: z.string().trim().max(2000).optional(),
  risks: z.string().trim().max(4000).optional(),
  comment: z.string().trim().max(2000).optional(),
});
export type PrecontractAction = z.infer<typeof PrecontractAction>;

export const ProjectCardView = z.object({
  projectId: z.uuid(),
  stage: z.string(),
  /** До подписания доходного договора: КП, расчёты и договорённости предварительные (ПФ-23) */
  preliminary: z.boolean(),
  /** Можно создавать заявки и расходные договоры: договор подписан или исключение одобрено */
  obligationsAllowed: z.boolean(),
  revenueContract: z
    .object({ id: z.uuid(), number: z.string(), signedDate: DateStr.nullable(), amount: Money.nullable() })
    .nullable(),
  requestNote: z.string().nullable(),
  tenderNote: z.string().nullable(),
  sourceNote: z.string().nullable(),
  costCalcAmount: Money.nullable(),
  costCalcNote: z.string().nullable(),
  costCalcByName: z.string().nullable(),
  costCalcAt: z.string().nullable(),
  volumesByName: z.string().nullable(),
  volumesAt: z.string().nullable(),
  /** Расчёт используется только с подтверждениями исполнителя и ПТО (ПФ-21) */
  calcUsable: z.boolean(),
  offerAmount: Money.nullable(),
  offerSentOn: DateStr.nullable(),
  exception: z.object({
    status: z.enum(Object.keys(EXCEPTION_STATUSES) as [ExceptionStatus, ...ExceptionStatus[]]),
    reason: z.string().nullable(),
    scope: z.string().nullable(),
    cost: Money.nullable(),
    terms: z.string().nullable(),
    risks: z.string().nullable(),
    byId: z.uuid().nullable(),
    byName: z.string().nullable(),
    at: z.string().nullable(),
    decisionByName: z.string().nullable(),
    decisionAt: z.string().nullable(),
    comment: z.string().nullable(),
  }),
  financing: z.array(
    FinancingRowInput.extend({
      id: z.uuid(),
      amount: Money.nullable(),
      source: z.string().nullable(),
      ratePct: z.number().nullable(),
      dateFrom: DateStr.nullable(),
      dateTo: DateStr.nullable(),
      note: z.string().nullable(),
      days: z.number().int().nullable(),
      cost: Money.nullable(),
    }),
  ),
  financingCost: Money.nullable(),
  card: z.array(
    z.object({
      id: z.uuid(),
      article: z.string(),
      counterpartyBin: z.string().nullable(),
      counterpartyName: z.string().nullable(),
      name: z.string(),
      unit: z.string(),
      quantity: z.number(),
      purchasePrice: Money.nullable(),
      salePrice: Money.nullable(),
      purchaseSum: Money.nullable(),
      saleSum: Money.nullable(),
    }),
  ),
  cardTotals: z.object({
    purchase: Money.nullable(),
    sale: Money.nullable(),
    marginPct: z.number().nullable(),
  }),
  /** Задача РП после подписания доходного договора: карточка и ФП за 5 рабочих дней (ПФ-25) */
  task: z.object({ due: DateStr, done: z.boolean(), overdue: z.boolean() }).nullable(),
  /** Суммы скрыты уровнем доступа к деньгам */
  moneyHidden: z.boolean(),
});
export type ProjectCardView = z.infer<typeof ProjectCardView>;

/* ─────────── «Требует внимания» в карточке проекта: находки всех разделов (ПФ-02, ПФ-03, ПМ-06) ─────────── */

/** Разделы, чьи находки попадают в блок: дела «Ждёт меня» и находки модулей (pm.attention_items) */
export const ATTENTION_SOURCES = {
  ...INBOX_SOURCES,
  portfolio: 'Проект',
  budget: 'Бюджет',
  psd: 'ПСД',
  resources: 'Ресурсы',
} as const;

export const ProjectAttentionItem = z.object({
  id: z.string(),
  /** Раздел: ключ ATTENTION_SOURCES или код модуля находки */
  source: z.string(),
  sourceTitle: z.string(),
  /** action — дело за человеком («Ждёт меня»), finding — находка раздела по проекту */
  kind: z.enum(['action', 'finding']),
  severity: z.enum(['critical', 'warning', 'info']),
  title: z.string(),
  details: z.string().nullable(),
  projectId: z.uuid().nullable(),
  projectCode: z.string().nullable(),
  due: DateStr.nullable(),
  overdue: z.boolean(),
  actionLabel: z.string(),
  actionPath: z.string(),
});
export type ProjectAttentionItem = z.infer<typeof ProjectAttentionItem>;

/** Сводка ПСД проекта: комплекты, производство, просрочки, открытые вопросы и замечания */
export const PsdDigest = z.object({
  sets: z.number().int(),
  inProduction: z.number().int(),
  expected: z.number().int(),
  overdue: z.number().int(),
  quarantine: z.number().int(),
  openQuestions: z.number().int(),
  openRemarks: z.number().int(),
});
export type PsdDigest = z.infer<typeof PsdDigest>;

/** Сводка ресурсов проекта на текущую неделю: потребность, нехватка людей, ресурсы без ставки */
export const ResourcesDigest = z.object({
  week: DateStr.nullable(),
  needPeople: z.number(),
  needMachines: z.number(),
  shortages: z.array(z.object({ name: z.string(), need: z.number(), available: z.number() })),
  missingRates: z.number().int(),
});
export type ResourcesDigest = z.infer<typeof ResourcesDigest>;

export const ProjectAttentionView = z.object({
  projectId: z.uuid(),
  items: z.array(ProjectAttentionItem),
  /** Сколько строк у каждого раздела — для фильтра на экране */
  sections: z.array(z.object({ source: z.string(), title: z.string(), count: z.number().int() })),
  /** null — нет права видеть раздел */
  psd: PsdDigest.nullable(),
  resources: ResourcesDigest.nullable(),
});
export type ProjectAttentionView = z.infer<typeof ProjectAttentionView>;

/** Стоимость кредита или гарантии за период: сумма × ставка / 365 × дни */
export function financingCost(
  amount: number,
  ratePct: number | null,
  from: string | null,
  to: string | null,
) {
  if (!ratePct || !from || !to) return { days: null, cost: null };
  const days = Math.max(0, Math.round((Date.parse(to) - Date.parse(from)) / 86_400_000));
  return { days, cost: Math.round(((amount * ratePct) / 100 / 365) * days * 100) / 100 };
}
